<template>
	<div
		v-loading="fetchingData"
		class="data-table-container position-relative"
	>
		<div class="data-table-top mb-3 d-flex align-items-center">
			<slot name="data-table-top">
				<div>
					<template v-for="(filter, filter_k) in filters">
						<b-badge
							v-for="(item, item_k) in filter.items"
							:key="`${filter.label.replace(' ', '_')}-${item_k}`"
							variant="light"
							class="text-primary py-1 px-3 mr-2 border border-primary d-inline-flex align-items-center shadow"
						>
							{{ filter.label || filter.prop }}: {{ item }}
							<b-button
								@click.stop.prevent="
									removeFilter(filter_k, item_k)
								"
								size="lg"
								variant="link"
								class="text-danger p-0 ml-2 rounded-circle"
							>
								<i class="fa fa-times"></i>
							</b-button>
						</b-badge>
					</template>
				</div>
			</slot>

			<slot name="data-table-top-right"></slot>
		</div>

		<b-card
			class="mb-0"
			body-classes="p-0"
			:class="{ 'shadow-none': fetchingData }"
			:bg-variant="variant"
		>
			<slot name="header">
				<template slot="header">
					<div
						class="d-flex justify-content-center justify-content-sm-between flex-wrap"
					>
						<div class="d-flex justify-content-center">
							<slot
								name="header-left"
								v-bind:selected="selectedRows"
							></slot>

							<slot name="header-bulk-delete">
								<b-button
									v-if="0 < selectedRows.length"
									@click.prevent="bulkDelete(selectedRows)"
									:disabled="
										selectedRows.length ? false : true
									"
									variant="link"
									size="sm"
									class="text-info bg-transparent py-1 px-0 font-weight-normal d-flex align-items-center shadow-none"
								>
									Delete
								</b-button>
							</slot>
						</div>

						<slot
							name="header-right"
							v-bind:selected="selectedRows"
						></slot>
					</div>
				</template>
			</slot>

			<div class="overflow-hidden">
				<el-table
					:data="tableData"
					:empty-text="
						fetchingData ? 'Please wait...' : `No ${label} found.`
					"
					:row-class-name="getRowClassName"
					row-key="id"
					header-row-class-name="thead-light"
					highlight-current-row
					@cell-click="onCellClick"
					@sort-change="onSortChange"
					@selection-change="onSelectionChange"
					ref="datatable"
				>
					<el-table-column
						v-if="selectable"
						type="selection"
						:selectable="isRowSelectable"
					></el-table-column>
					<el-table-column
						v-for="headerColumn in headerColumns"
						:key="`column-${headerColumn.label}`"
						:type="headerColumn.type"
						:class-name="headerColumn.class || null"
						:label-class-name="headerColumn.labelClass || null"
						:sort-by="headerColumn.sortBy || headerColumn.prop"
						:sort-orders="['ascending', 'descending']"
						sortable="custom"
						v-bind="headerColumn"
					>
						<template slot="header">
							{{ headerColumn.label }}

							<template v-if="headerColumn.searchable">
								<b-button
									@click.stop
									:id="`search-popover-${headerColumn.label.replace(
										' ',
										'_'
									)}`"
									class="el-table-column--search position-absolute p-0 text-primary bg-transparent p-1 border-0 shadow-none"
								>
									<b-icon
										icon="search"
										font-scale="0.95"
										class="mr-0"
									></b-icon>
								</b-button>
							</template>
						</template>
						<template slot-scope="scope">
							<slot
								:name="`cell_${headerColumn.prop}`"
								:label="headerColumn.label"
								:value="
									getCellValue(headerColumn.prop, scope.row)
								"
								:row="scope.row"
								:index="scope.$index"
								:total="tableData.length"
							>
								{{ getCellValue(headerColumn.prop, scope.row) }}
							</slot>
						</template>
					</el-table-column>
					<el-table-column
						v-if="0 <= actionsAllowed.indexOf('delete')"
						:width="`${actionsWidth}px`"
						fixed="right"
						class-name="no-label"
					>
						<div
							slot-scope="{ $index, row }"
							class="text-lg d-flex align-items-center justify-content-center"
						>
							<slot name="row-actions" v-bind="row">
								<a
									href='#'
									class="text-lighter"
									:class="{'disabled-action' : !canDeleteRow(row)}"
									v-b-tooltip.hover
									:title="!canDeleteRow(row) ? deleteDisabledReason(row) : ''"
									@click.prevent.stop="canDeleteRow(row) &&
										deleteItem($index, row)
									"
								>
									<b-icon icon="trash"></b-icon>
								</a>
							</slot>
						</div>
					</el-table-column>
				</el-table>
			</div>

			<template v-for="headerColumn in headerColumns">
				<template v-if="headerColumn.searchable">
					<b-popover
						:key="`search-popover-${headerColumn.label.replace(
							' ',
							'_'
						)}`"
						:target="`search-popover-${headerColumn.label.replace(
							' ',
							'_'
						)}`"
						triggers="click blur"
						:custom-class="
							'date' === headerColumn.searchType ? 'p-0' : 'p-2'
						"
					>
						<b-calendar
							v-if="'date' === headerColumn.searchType"
							v-model="models.filter"
							@selected="addFilter(headerColumn.label)"
						></b-calendar>
						<b-form
							v-else
							@submit.prevent="addFilter(headerColumn.label)"
							form-class="p-2"
						>
							<b-form-input
								v-model="models.filter"
								size="sm"
								name="filter"
								:placeholder="`Search by ${headerColumn.label}`"
							></b-form-input>

							<div class="p-0 mt-2 d-flex">
								<base-button
									type="primary"
									native-type="submit"
									size="sm"
									class="ml-auto"
									>Apply</base-button
								>
							</div>
						</b-form>
					</b-popover>
				</template>
			</template>

			<div
				slot="footer"
				class="d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap"
			>
				<div class="font-family-secondary text-sm">
					Show
					<el-select
						class="select-primary pagination-select mx-2"
						v-model="pagination.page_size"
						size="mini"
						placeholder="Per page"
					>
						<el-option
							class="select-primary"
							v-for="item in pagination.perPageOptions"
							:key="`per-page-${item}`"
							:label="item"
							:value="item"
						>
						</el-option>
					</el-select>
					entries
				</div>

				<BasePagination
					:current="pagination.page"
					:per-page="pagination.page_size"
					:total="total"
					@change="onPageChange($event)"
					class="pagination-no-border"
				/>
			</div>
		</b-card>
	</div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import { dataTableMixin, userMixin } from "@mixins";
import { BasePagination } from "@/components";

export default {
	name: "base-data-table",
	mixins: [dataTableMixin, userMixin],
	components: {
		BasePagination,
		[Select.name]: Select,
		[Option.name]: Option,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
	},
};
</script>

<style lang="scss">
.el-loading {
	&-spinner {
		width: 42px;
		margin-right: auto;
		margin-left: auto;
		right: 0;
		left: 0;
	}
}
</style>
